import React, { useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import PageHero from "../components/PageHero";
import QuoteForm from "../components/QuoteForm";
import CallToActionBanner from "../components/CallToActionBanner";
import { categories, HIDDEN_CATEGORIES, COUNTRY_NAMES } from "../mock";
import { CheckCircle2, ArrowRight, ArrowDown, Package, FileText, Ship } from "lucide-react";
import SEO from "../components/SEO";

function slugifyAnchor(s) {
  return encodeURIComponent(s);
}

function ProductCard({ product, index }) {
  const reverse = index % 2 === 1;
  const cols = product.specsColumns; // custom columns (Slag/Gabbro)
  return (
    <article
      id={slugifyAnchor(product.name)}
      className="scroll-mt-28 fade-in"
    >
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Image */}
        <div className={`relative ${reverse ? "lg:order-2" : ""}`}>
          <div
            className="absolute -inset-3 rounded-2xl -z-10 opacity-70"
            style={{ background: "linear-gradient(135deg, var(--brand-100) 0%, var(--brand-50) 100%)" }}
          />
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border shadow-sm bg-muted">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700"
            />
            {/* Light watermark logo overlay */}
            <div className="absolute bottom-3 right-3 pointer-events-none">
              <img
                src="/logo.png"
                alt=""
                aria-hidden="true"
                className="h-8 md:h-10 w-auto object-contain opacity-70 drop-shadow-lg"
                style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.35))" }}
              />
            </div>
            <div className="absolute top-4 left-4 max-w-[80%] flex flex-wrap items-center gap-1.5">
              {/* Product name badge */}
              <span
                className="px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider text-white"
                style={{ background: "var(--brand-700)" }}
              >
                {product.name.split(" (")[0].split(" /")[0]}
              </span>
              {/* Origin flag + country name chips */}
              {product.origins && product.origins.length > 0 && product.origins.map((code) => (
                <span
                  key={code}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-md border border-white shadow-sm text-[11px] font-semibold"
                  style={{ color: "var(--brand-700)" }}
                  data-testid={`origin-chip-${code.toLowerCase()}`}
                >
                  <img
                    src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w80/${code.toLowerCase()}.png 2x`}
                    alt={COUNTRY_NAMES[code] || code}
                    className="w-4 h-auto rounded-[2px] shadow-sm"
                    loading="lazy"
                  />
                  {COUNTRY_NAMES[code] || code}
                </span>
              ))}
            </div>
            {product.export && (
              <div className="absolute bottom-4 left-4 right-16 px-4 py-2.5 rounded-lg bg-white/90 backdrop-blur-md border border-white text-xs text-foreground/80 flex items-center gap-2">
                <Ship size={14} style={{ color: "var(--brand-700)" }} />
                <span className="font-medium">{product.export}</span>
              </div>
            )}
          </div>
        </div>

        {/* Text */}
        <div className={`${reverse ? "lg:order-1" : ""}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--brand-100)" }}>
              <Package size={16} style={{ color: "var(--brand-700)" }} />
            </span>
            {product.spec && (
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                {product.spec}
              </span>
            )}
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-brand leading-tight" style={{ color: "var(--brand-700)" }}>
            {product.name}
          </h3>
          {/* Origins inline row (flag + country name) */}
          {product.origins && product.origins.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Origin:</span>
              {product.origins.map((code) => (
                <span
                  key={code}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-border bg-muted/40 text-xs font-semibold"
                  style={{ color: "var(--brand-700)" }}
                  data-testid={`origin-inline-${code.toLowerCase()}`}
                >
                  <img
                    src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w80/${code.toLowerCase()}.png 2x`}
                    alt={COUNTRY_NAMES[code] || code}
                    className="w-4 h-auto rounded-[2px]"
                    loading="lazy"
                  />
                  {COUNTRY_NAMES[code] || code}
                </span>
              ))}
            </div>
          )}
          {product.body.split("\n\n").map((para, i) => (
            <p key={i} className="mt-4 text-foreground/75 leading-relaxed">{para}</p>
          ))}

          {product.highlights && product.highlights.length > 0 && (
            <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
              {product.highlights.map((h, hi) => (
                <li key={hi} className="flex items-start gap-2.5 text-sm text-foreground/80">
                  <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--brand-500)" }} />
                  {h}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-7 flex flex-wrap gap-3">
            {product.detailPage ? (
              <Link
                to={product.detailPage}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md font-medium text-white"
                style={{ backgroundColor: "var(--brand-700)" }}
              >
                View Full Details <ArrowRight size={15} />
              </Link>
            ) : (
              <Link
                to="/contact-us"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md font-medium text-white"
                style={{ backgroundColor: "var(--brand-700)" }}
              >
                Request a Quote <ArrowRight size={15} />
              </Link>
            )}
            {product.specSheet ? (
              <a
                href={product.specSheet}
                download
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md font-medium border border-border hover:border-brand transition"
                style={{ color: "var(--brand-700)" }}
              >
                <FileText size={15} /> Download Spec Sheet
              </a>
            ) : (
              <a
                href={`#inquiry`}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md font-medium border border-border hover:border-brand transition"
                style={{ color: "var(--brand-700)" }}
              >
                <FileText size={15} /> Spec Sheet
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Grades Available table (Bitumen etc.) */}
      {product.gradesTable && (
        <div className="mt-10">
          <div className="rounded-2xl border border-border bg-white overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-border flex items-center gap-3" style={{ background: "var(--brand-50)" }}>
              <FileText size={17} style={{ color: "var(--brand-700)" }} />
              <h4 className="font-display font-semibold text-brand" style={{ color: "var(--brand-700)" }}>
                {product.gradesTable.title}
              </h4>
            </div>
            {product.gradesTable.intro && (
              <p className="px-6 pt-5 text-sm text-foreground/75 leading-relaxed">
                {product.gradesTable.intro}
              </p>
            )}
            <div className="overflow-x-auto px-2 py-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    {product.gradesTable.columns.map((c, ci) => (
                      <th key={ci} className={`px-4 py-3 ${ci === 0 ? "w-28" : ""}`}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {product.gradesTable.rows.map((row, ri) => (
                    <tr key={ri} className="border-b border-border last:border-0 hover:bg-muted/30 transition">
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className={`px-4 py-3 ${ci === 0 ? "font-semibold" : "text-foreground/75"}`}
                          style={ci === 0 ? { color: "var(--brand-700)" } : {}}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {product.gradesTable.note && (
              <p className="px-6 py-4 text-xs text-muted-foreground italic border-t border-border">
                ({product.gradesTable.note})
              </p>
            )}
          </div>
        </div>
      )}

      {/* Packaging & Logistics + Quality Assurance (two-column on lg) */}
      {(product.packaging || product.qualityAssurance) && (
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {product.packaging && (
            <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-border flex items-center gap-3" style={{ background: "var(--brand-50)" }}>
                <Package size={17} style={{ color: "var(--brand-700)" }} />
                <h4 className="font-display font-semibold" style={{ color: "var(--brand-700)" }}>
                  Packaging & Logistics
                </h4>
              </div>
              <ul className="p-6 space-y-3">
                {product.packaging.map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/80 leading-relaxed">
                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--brand-500)" }} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {product.qualityAssurance && (
            <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-border flex items-center gap-3" style={{ background: "var(--brand-50)" }}>
                <FileText size={17} style={{ color: "var(--brand-700)" }} />
                <h4 className="font-display font-semibold" style={{ color: "var(--brand-700)" }}>
                  Quality Assurance
                </h4>
              </div>
              <p className="p-6 text-sm text-foreground/80 leading-relaxed">
                {product.qualityAssurance}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Specification table (below the image/text grid, always after) */}
      {product.specs && product.specs.length > 0 && (
        <div className="mt-10">
          <div className="rounded-2xl border border-border bg-white overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-border flex items-center gap-3" style={{ background: "var(--brand-50)" }}>
              <FileText size={17} style={{ color: "var(--brand-700)" }} />
              <h4 className="font-display font-semibold text-brand" style={{ color: "var(--brand-700)" }}>
                Typical Specifications
              </h4>
              <span className="ml-auto text-[11px] uppercase tracking-wider text-muted-foreground">
                Customisable to buyer spec
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    {cols ? (
                      cols.map((c, ci) => (
                        <th key={ci} className={`px-6 py-3 ${ci === 0 ? "w-16" : ""}`}>{c}</th>
                      ))
                    ) : (
                      <>
                        <th className="px-6 py-3 w-16">#</th>
                        <th className="px-6 py-3">Parameter</th>
                        <th className="px-6 py-3">Chemical Symbol</th>
                        <th className="px-6 py-3">Typical Range</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {product.specs.map((row, ri) => (
                    <tr key={ri} className="border-b border-border last:border-0 hover:bg-muted/30 transition">
                      <td className="px-6 py-3 text-muted-foreground font-mono">{String(ri + 1).padStart(2, "0")}</td>
                      <td className="px-6 py-3 font-medium text-foreground">{row.p}</td>
                      <td className="px-6 py-3 text-foreground/70 font-mono text-[13px]">{row.c}</td>
                      {row.u !== undefined && (
                        <td className="px-6 py-3 text-foreground/70 font-mono text-[13px]">{row.u}</td>
                      )}
                      <td className="px-6 py-3 font-semibold" style={{ color: "var(--brand-700)" }}>{row.r}</td>
                      {row.s !== undefined && (
                        <td className="px-6 py-3 text-foreground/70 text-[13px]">{row.s}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Detached compliance / customisation callout — visually separate from the spec table */}
          {product.specsFooter ? (
            <div
              className="mt-5 rounded-2xl border-2 px-6 py-5 text-center text-base md:text-lg font-semibold leading-relaxed shadow-sm"
              style={{ background: "var(--brand-50)", color: "var(--brand-700)", borderColor: "var(--brand-100)" }}
              data-testid="spec-footer-callout"
            >
              {product.specsFooter}
            </div>
          ) : (
            <div
              className="mt-5 rounded-2xl border-2 px-6 py-5 text-center text-lg md:text-xl font-bold leading-relaxed shadow-sm"
              style={{ background: "var(--brand-50)", color: "var(--brand-700)", borderColor: "var(--brand-100)" }}
              data-testid="spec-footer-callout"
            >
              * Based on your customised requirements, we can source and supply special {product.name.split(" (")[0].split(" /")[0]} grades.
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function SimpleProductBlock({ product, index }) {
  return (
    <div id={slugifyAnchor(product.name)} className="p-7 rounded-xl border border-border bg-muted/20 fade-in scroll-mt-28">
      <div className="flex items-start gap-4">
        <span
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-display font-semibold"
          style={{ background: "var(--brand-700)" }}
        >
          {String.fromCharCode(97 + index)}
        </span>
        <div className="flex-1">
          <h3 className="font-display text-lg font-semibold text-brand" style={{ color: "var(--brand-700)" }}>
            {product.name}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{product.body}</p>
          {product.detailPage && (
            <Link
              to={product.detailPage}
              className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-md font-medium text-white text-sm"
              style={{ backgroundColor: "var(--brand-700)" }}
            >
              View Full Details <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CategoryDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const cat = categories.find((c) => c.slug === slug);

  // Smooth-scroll to anchor if hash present
  useEffect(() => {
    if (location.hash) {
      const id = decodeURIComponent(location.hash.replace("#", ""));
      const el = document.getElementById(encodeURIComponent(id)) || document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
  }, [location.hash, slug]);

  if (!cat) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold text-brand" style={{ color: "var(--brand-700)" }}>Category not found</h1>
        <Link to="/product-categories" className="mt-6 inline-flex items-center gap-2 text-brand-accent font-semibold" style={{ color: "var(--brand-500)" }}>
          Back to categories <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  const hasImages = cat.products.every((p) => !!p.image);

  return (
    <>
      <SEO
        title={cat.title}
        description={`${cat.short} Sourced through trusted manufacturers with full documentation, quality certificates and reliable global logistics.`}
        path={`/products/${cat.slug}`}
        image={cat.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `${cat.title} — Product Range`,
          itemListElement: cat.products.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Product",
              name: p.name,
              description: p.body,
              image: p.image,
              brand: { "@type": "Brand", name: "Ruken Alazdehar" },
            },
          })),
        }}
      />
      <PageHero
        title={cat.title}
        subtitle={cat.short}
        breadcrumbs={[
          { label: "Commodities", to: "/product-categories" },
          { label: cat.title },
        ]}
        bgImage={cat.image}
      />

      {/* Product navigator (chips) */}
      <section className="bg-white border-b border-border sticky top-20 z-30 backdrop-blur-md bg-white/90">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3 overflow-x-auto no-scrollbar">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold flex-shrink-0" style={{ color: "var(--brand-500)" }}>
            Jump to:
          </span>
          {cat.products.map((p) => (
            <a
              key={p.name}
              href={`#${slugifyAnchor(p.name)}`}
              className="text-xs font-medium px-3 py-1.5 rounded-full border border-border hover:border-brand transition whitespace-nowrap"
              style={{ color: "var(--brand-700)" }}
            >
              {p.name.split(" (")[0].split(" —")[0]}
            </a>
          ))}
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>
            Product Range
          </p>
          <h2 className="mt-2 font-display text-3xl md:text-5xl font-bold text-brand leading-tight" style={{ color: "var(--brand-700)" }}>
            {cat.title}
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            {cat.intro || `${cat.short} Each product is sourced through trusted manufacturers and shipped with full documentation, quality certificates and reliable logistics — ready for FOB, CFR or CIF delivery to your destination port.`}
          </p>
          <a href={`#${slugifyAnchor(cat.products[0].name)}`} className="mt-8 inline-flex items-center gap-2 text-sm font-semibold link-underline" style={{ color: "var(--brand-700)" }}>
            Explore the range <ArrowDown size={15} />
          </a>
        </div>
      </section>

      {/* Products list */}
      <section className="pb-20 md:pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-20 md:space-y-28">
          {hasImages
            ? cat.products.map((p, i) => <ProductCard key={p.name} product={p} index={i} />)
            : cat.products.map((p, i) => <SimpleProductBlock key={p.name} product={p} index={i} />)}
        </div>
      </section>

      {/* Cross-link */}
      <section className="py-14 bg-muted/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>Continue browsing</p>
              <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold text-brand" style={{ color: "var(--brand-700)" }}>Explore other categories</h3>
            </div>
            <Link to="/product-categories" className="inline-flex items-center gap-2 text-sm font-semibold link-underline" style={{ color: "var(--brand-700)" }}>
              View all categories <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {categories.filter((c) => c.slug !== slug && !HIDDEN_CATEGORIES.includes(c.slug)).map((c) => (
              <Link
                key={c.slug}
                to={`/products/${c.slug}`}
                className="group bg-white rounded-xl border border-border overflow-hidden card-hover"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <p className="mt-1 font-display font-semibold text-sm text-brand line-clamp-1" style={{ color: "var(--brand-700)" }}>{c.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CallToActionBanner />

      <section id="inquiry" className="section-padding bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>Get a Quote</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand mt-2" style={{ color: "var(--brand-700)" }}>Fill the inquiry form</h2>
            <p className="mt-3 text-muted-foreground">Share your specs and we'll respond with the best quote within one business day.</p>
          </div>
          <QuoteForm specPlaceholder={
            cat.slug === "category-b-coal" ? "e.g. Steam coal, GAR 5,500 kcal/kg, size 0–50mm, TM 15%" :
            cat.slug === "category-f-petroleum-products" ? "e.g. Bitumen 60/70, ASTM D946, 25 kg drums / bulk" :
            "e.g. Grey Clinker, size 25–40mm, C3S ≥ 58, moisture nil"
          } />
        </div>
      </section>
    </>
  );
}
